import type {
  FolderUploadFailureReason,
  FolderUploadFailureStage,
  FolderUploadFileState,
  FolderUploadFileStatus,
  FolderUploadJobState,
} from './types';

/** How far back the transfer rate looks. */
export const FOLDER_UPLOAD_RATE_WINDOW_MS = 15_000;
/** Samples closer together than this replace each other. */
const RATE_SAMPLE_SPACING_MS = 500;

export type FolderUploadFileInput = {
  name: string;
  relativeDirectoryPath: string | null;
  sizeBytes: number;
  /** A file refused before upload (too large, invalid folder name). */
  failure?: { reason: FolderUploadFailureReason } | null;
};

export function createFolderUploadJob(args: {
  id: string;
  orgSlug: string;
  targetDirectoryPath: string;
  selectionKind: 'folder' | 'files';
  files: ReadonlyArray<FolderUploadFileInput>;
  blockedReason?: FolderUploadFailureReason | null;
  emptyFolderCount?: number;
  now: number;
}): FolderUploadJobState {
  const blockedReason = args.blockedReason ?? null;
  return {
    id: args.id,
    orgSlug: args.orgSlug,
    targetDirectoryPath: args.targetDirectoryPath,
    selectionKind: args.selectionKind,
    files: args.files.map((file, id) => {
      const reason = blockedReason ?? file.failure?.reason ?? null;
      return {
        id,
        name: file.name,
        relativeDirectoryPath: file.relativeDirectoryPath,
        sizeBytes: file.sizeBytes,
        loadedBytes: 0,
        status: reason ? 'failed' : 'queued',
        reason,
        stage: reason ? 'validate' : null,
        retryable: reason === null,
      };
    }),
    blockedReason,
    running: false,
    cancelRequested: false,
    startedAt: args.now,
    finishedAt: blockedReason ? args.now : null,
    emptyFolders: { total: args.emptyFolderCount ?? 0, failed: 0 },
    transferredBytes: 0,
    rateSamples: [],
  };
}

function withSample(
  job: FolderUploadJobState,
  transferredBytes: number,
  now: number
): FolderUploadJobState {
  const recent = job.rateSamples.filter(
    (sample) => sample.at >= now - FOLDER_UPLOAD_RATE_WINDOW_MS
  );
  const last = recent.at(-1);
  const kept =
    last && now - last.at < RATE_SAMPLE_SPACING_MS && recent.length > 1
      ? recent.slice(0, -1)
      : recent;
  return {
    ...job,
    transferredBytes,
    rateSamples: [...kept, { at: now, bytes: transferredBytes }],
  };
}

function updateFiles(
  job: FolderUploadJobState,
  ids: ReadonlyArray<number>,
  update: (file: FolderUploadFileState) => FolderUploadFileState
): FolderUploadJobState {
  const wanted = new Set(ids);
  let changed = false;
  const files = job.files.map((file) => {
    if (!wanted.has(file.id)) return file;
    const next = update(file);
    if (next !== file) changed = true;
    return next;
  });
  return changed ? { ...job, files } : job;
}

function inStatus(
  file: FolderUploadFileState,
  statuses: ReadonlyArray<FolderUploadFileStatus>
): boolean {
  return statuses.includes(file.status);
}

export function startFolderUploadFile(
  job: FolderUploadJobState,
  id: number
): FolderUploadJobState {
  return updateFiles(job, [id], (file) =>
    file.status === 'queued'
      ? { ...file, status: 'uploading', loadedBytes: 0 }
      : file
  );
}

/** Bytes sent for one in-flight file; never past its size, never backwards. */
export function setFolderUploadFileProgress(
  job: FolderUploadJobState,
  id: number,
  loadedBytes: number,
  now: number
): FolderUploadJobState {
  const file = job.files[id];
  if (!file || file.status !== 'uploading') return job;
  const loaded = Math.min(file.sizeBytes, Math.max(0, loadedBytes));
  if (loaded <= file.loadedBytes) return job;
  const delta = loaded - file.loadedBytes;
  const next = updateFiles(job, [id], (entry) => ({
    ...entry,
    loadedBytes: loaded,
  }));
  return withSample(next, job.transferredBytes + delta, now);
}

/** Every byte of these files is stored; they wait to be filed. */
export function markFolderUploadFilesRegistering(
  job: FolderUploadJobState,
  ids: ReadonlyArray<number>,
  now: number
): FolderUploadJobState {
  let delta = 0;
  const next = updateFiles(job, ids, (file) => {
    if (file.status !== 'uploading') return file;
    delta += file.sizeBytes - file.loadedBytes;
    return { ...file, status: 'registering', loadedBytes: file.sizeBytes };
  });
  return delta > 0 ? withSample(next, job.transferredBytes + delta, now) : next;
}

export function completeFolderUploadFiles(
  job: FolderUploadJobState,
  ids: ReadonlyArray<number>
): FolderUploadJobState {
  return updateFiles(job, ids, (file) =>
    inStatus(file, ['uploading', 'registering'])
      ? {
          ...file,
          status: 'done',
          loadedBytes: file.sizeBytes,
          reason: null,
          stage: null,
        }
      : file
  );
}

export function failFolderUploadFiles(
  job: FolderUploadJobState,
  ids: ReadonlyArray<number>,
  reason: FolderUploadFailureReason,
  stage: FolderUploadFailureStage,
  /** Fails the same way on Retry (default: by reason); Retry is not offered. */
  final = DETERMINISTIC_FAILURES.has(reason)
): FolderUploadJobState {
  return updateFiles(job, ids, (file) =>
    inStatus(file, ['queued', 'uploading', 'registering'])
      ? {
          ...file,
          status: 'failed',
          loadedBytes: 0,
          reason,
          stage,
          ...(final ? { retryable: false } : {}),
        }
      : file
  );
}

const DETERMINISTIC_FAILURES = new Set<FolderUploadFailureReason>([
  'active_content',
  'too_large',
  'permission',
]);

/**
 * Cancels files that have not finished sending. A file being filed or
 * already done stays: cancelling never removes a finished file.
 */
export function cancelFolderUploadFiles(
  job: FolderUploadJobState,
  ids: ReadonlyArray<number>
): FolderUploadJobState {
  return updateFiles(job, ids, (file) =>
    inStatus(file, ['queued', 'uploading'])
      ? {
          ...file,
          status: 'cancelled',
          loadedBytes: 0,
          reason: null,
          stage: null,
        }
      : file
  );
}

export function cancelAllFolderUploadFiles(
  job: FolderUploadJobState
): FolderUploadJobState {
  const next = cancelFolderUploadFiles(
    job,
    job.files.map((file) => file.id)
  );
  return { ...next, cancelRequested: true };
}

/** Ids that a retry may send again: failed or cancelled, and retryable. */
export function retryableFolderUploadFileIds(
  job: FolderUploadJobState,
  statuses: ReadonlyArray<FolderUploadFileStatus> = ['failed']
): number[] {
  return job.files
    .filter((file) => file.retryable && inStatus(file, statuses))
    .map((file) => file.id);
}

/** Puts failed or cancelled files back in the queue for another try. */
export function requeueFolderUploadFiles(
  job: FolderUploadJobState,
  ids: ReadonlyArray<number>
): FolderUploadJobState {
  const next = updateFiles(job, ids, (file) =>
    file.retryable && inStatus(file, ['failed', 'cancelled'])
      ? {
          ...file,
          status: 'queued',
          loadedBytes: 0,
          reason: null,
          stage: null,
        }
      : file
  );
  return next === job
    ? job
    : { ...next, cancelRequested: false, finishedAt: null };
}

export function setFolderUploadJobRunning(
  job: FolderUploadJobState,
  running: boolean,
  now: number
): FolderUploadJobState {
  if (job.running === running) return job;
  return running
    ? { ...job, running, finishedAt: null, rateSamples: [] }
    : { ...job, running, finishedAt: now };
}

export function recordFolderUploadEmptyFolders(
  job: FolderUploadJobState,
  failed: number
): FolderUploadJobState {
  return { ...job, emptyFolders: { ...job.emptyFolders, failed } };
}
